import {
    CircleButton,
    Dropdown as DSDropdown,
    DropdownProps as DSDropdownProps,
} from '@placer-ui/components';
import { Button, Dropdown } from 'antd';
import type { DropDownProps } from 'antd/lib/dropdown';
import classnames from 'classnames';
import { MenuInfo } from 'rc-menu/lib/interface';
import React, {
    Children,
    cloneElement,
    ReactElement,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import styles from './three-dots-menu.module.scss';

import { DotsMoreIcon } from 'components/assets/Icons/Icons';
import { getFeatureFlags } from 'core/flow-control';
import { Menu } from './menu';

type MenuChildrenType = ReactElement | (ReactElement | null | false | undefined)[];

type ThreeDotsMenuProps = {
    children: MenuChildrenType;
    disabled?: boolean;
    menuClassName?: string;
    className?: string;
    overlayClassName?: string;
    trigger?: DropDownProps['trigger'];
    placement?: DropDownProps['placement'];
    dropdownAlign?: DropDownProps['align'];
    DSdropdownAlign?: DSDropdownProps['align'];

    triggerButton?: ReactNode;
    dotsMenuClassName?: string;
    /** getPopupContainer as a number is the number of parents to iterate from the trigger-node */
    getPopupContainer?: number | DropDownProps['getPopupContainer'];
};

const defaults = {
    disabled: false,
    trigger: ['click'] as DropDownProps['trigger'],
};

export const ThreeDotsMenu = ({
    children,
    menuClassName,
    className,
    overlayClassName,
    trigger = defaults.trigger,
    disabled = defaults.disabled,
    placement = 'bottomRight',
    dropdownAlign,
    DSdropdownAlign = 'end',
    triggerButton,
    getPopupContainer,
    dotsMenuClassName,
}: ThreeDotsMenuProps) => {
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const mounted = useRef(false);
    const { accessibility_components_ff } = getFeatureFlags();

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const setDropdownVisibility = (isVisible: boolean) => {
        if (mounted.current) {
            setIsDropdownVisible(isVisible);
        }
    };

    const onButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const btnClassNames = classnames(styles.dotsMenu, dotsMenuClassName, {
        [styles.dotsMenuActive]: isDropdownVisible,
        [styles.threeDotsAjustments]: accessibility_components_ff,
    });

    const menuClassNames = classnames(styles.menu, menuClassName);

    const renderMenuItems = (children: MenuChildrenType) => {
        return Children.map(children, (child, i) =>
            child
                ? cloneElement(child, {
                      ...child.props,
                      className: classnames(child.props.className, {
                          [styles.menuItemLast]: i === Children.count(children) - 1,
                      }),
                      onClick: (e: MenuInfo) => {
                          // setTimeout is being used to allow Antd-Menu
                          // to unmounted peacefully before the callback fires
                          setTimeout(() => {
                              child.props.onClick(e);
                              setDropdownVisibility(false);
                          }, 100);
                      },
                  })
                : null,
        );
    };

    // find the container by traveling up the DOM tree
    if (typeof getPopupContainer === 'number') {
        let parentsCounter = getPopupContainer;
        getPopupContainer = (triggerNode) => {
            let container: HTMLElement | null = triggerNode;
            while (parentsCounter-- > 0 && container.parentElement !== null) {
                container = container.parentElement;
            }
            return container;
        };
    }

    const Overlay = <Menu className={menuClassNames}>{renderMenuItems(children)}</Menu>;

    if (accessibility_components_ff) {
        return (
            <div className={classnames(styles.threeDotsMenuWrapper, className)}>
                <DSDropdown
                    trigger={
                        triggerButton || (
                            <div className={btnClassNames}>
                                <CircleButton
                                    aria-label="More options"
                                    icon={<DotsMoreIcon />}
                                    testId="three-dots-menu-trigger"
                                    variant="default"
                                />
                            </div>
                        )
                    }
                    open={isDropdownVisible}
                    onOpenChange={setDropdownVisibility}
                    disabled={disabled}
                    align={DSdropdownAlign}
                >
                    {Overlay}
                </DSDropdown>
            </div>
        );
    }

    return (
        <div className={classnames(styles.threeDotsMenuWrapper, className)}>
            <Dropdown
                trigger={trigger}
                visible={isDropdownVisible}
                placement={placement}
                overlayClassName={overlayClassName}
                onVisibleChange={setDropdownVisibility}
                disabled={disabled}
                align={dropdownAlign}
                overlay={Overlay}
                getPopupContainer={getPopupContainer}
            >
                {triggerButton || (
                    <Button
                        type="ghost"
                        shape="circle"
                        data-testid="three-dots-menu-trigger"
                        className={btnClassNames}
                        onClick={onButtonClick}
                        aria-label="More options"
                        icon={<DotsMoreIcon />}
                    />
                )}
            </Dropdown>
        </div>
    );
};
